import { useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { useSessao } from '@/modulos/sessao/consultas';

import { explicarFalha, useGravarMotivo, useMotivos } from './consultas';
import type { MotivoDaApi } from './api';
import { usePaginacao } from './paginacao';
import { codigoDoNome, fraseDoErro } from './texto';
import { TurnoDaEmpresa } from './TurnoDaEmpresa';

/**
 * As causas de parada — o catálogo, pela tela.
 *
 * ## Por que esta tela precisou existir junto com a regra
 *
 * A migração 0014 passou a exigir causa catalogada em `Parada` e
 * `Em manutenção`. Sem uma tela de catálogo, isso viraria um beco: o formulário
 * de situação pediria uma causa, o seletor viria vazio, e a única saída seria
 * `insert` no banco. É exatamente a cicatriz que o módulo 08 já tinha — tanque
 * e posto só entravam por SQL, e `docs/PENDENCIAS.md` registrava que, com o
 * banco vazio, o módulo não tinha o que mostrar.
 *
 * Regra que ficou desta vez: **regra nova que exige um cadastro nasce junto com
 * a tela desse cadastro**, ou nasce bloqueando quem não tem acesso ao banco.
 *
 * ## Evitável é o campo que faz o catálogo valer alguma coisa
 *
 * A oportunidade que o v6 dá ao módulo 03 é, com estas palavras, "reduzir
 * paradas **evitáveis**". Sem separar chuva de bomba quebrada, o relatório soma
 * o que a empresa pode atacar com o que ela não pode, e o total não sugere ação
 * nenhuma. Por isso o campo é obrigatório e não tem padrão: um padrão aqui
 * seria uma opinião disfarçada de conveniência.
 *
 * ## Não há excluir, e isso é do motor
 *
 * O cadastro genérico do backend não tem `DELETE`, por decisão registrada lá.
 * Aqui a razão é específica: causa que já foi usada não pode sumir, senão o
 * histórico perde o agrupamento — as horas continuam, e a pergunta "de quê?"
 * deixa de ter resposta. Tirar de circulação é arquivar.
 */

/** Os campos que têm regra, na ordem da janela: é a ordem do foco quando o envio recusa. */
const CAMPOS_COM_REGRA = ['nome', 'codigo', 'evitavel'] as const;
type CampoComRegra = (typeof CAMPOS_COM_REGRA)[number];
type Erros = Readonly<Record<CampoComRegra, string | null>>;
const SEM_ERRO: Erros = { codigo: null, nome: null, evitavel: null };

/**
 * As mesmas regras de antes, UMA POR CAMPO — é o que permite validar ao sair
 * do campo (p.16; R01, p.28) em vez de só no envio.
 */
function mensagemDe(campo: CampoComRegra, valores: { codigo: string; nome: string; evitavel: string }): string | null {
  switch (campo) {
    case 'codigo':
      /* O MESMO formato que o banco cobra (`check (codigo ~ '^[A-Z0-9][A-Z0-9-]*$')`).
         Repetido aqui para a recusa chegar com o nome do campo em vez de virar
         violação de check — que na tela apareceria como erro do sistema. */
      return /^[A-Z0-9][A-Z0-9-]*$/.test(valores.codigo.trim())
        ? null
        : 'Use letras maiúsculas, números e hífen. Exemplo: BOMBA-HIDRAULICA.';
    case 'nome':
      return valores.nome.trim().length === 0 ? 'Escreva o nome da causa.' : null;
    case 'evitavel':
      return valores.evitavel === '' ? 'Diga se esta parada é evitável ou não.' : null;
  }
}

export function PaginaCausas() {
  const base = useId();
  const motivos = useMotivos();
  const gravar = useGravarMotivo();
  /* Cadastrar e alterar causa só com `motivo_de_parada.gerir` (prova do marco,
     07/10/2026): quem só lê a operação vê o vocabulário, sem os botões. */
  const sessao = useSessao();
  const podeGerir = (sessao.data?.permissoes ?? []).includes('motivo_de_parada.gerir');
  const todas = useMemo(() => motivos.data ?? [], [motivos.data]);
  const { visiveis, rodape } = usePaginacao(todas);

  const [editando, setEditando] = useState<MotivoDaApi | null>(null);
  const [aberto, setAberto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [evitavel, setEvitavel] = useState<'' | 'sim' | 'nao'>('');
  const [ativo, setAtivo] = useState(true);
  /* O CÓDIGO ACOMPANHA O NOME até a pessoa escrever nele (31/10/2026). */
  const [codigoTocado, setCodigoTocado] = useState(false);
  const [erros, setErros] = useState<Erros>(SEM_ERRO);
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);

  /** Validação ao SAIR do campo, e não a cada tecla (p.16). */
  function conferir(campo: CampoComRegra) {
    setErros((atual) => ({ ...atual, [campo]: mensagemDe(campo, { codigo, nome, evitavel }) }));
  }

  /** Digitar apaga SÓ o erro do próprio campo: o dos outros fica até ser corrigido. */
  function limpar(campo: CampoComRegra) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  const invalido = (campo: CampoComRegra) =>
    erros[campo] ? { 'aria-invalid': true as const, 'aria-describedby': `${base}-${campo}-erro` } : {};

  function abrir(linha: MotivoDaApi | null) {
    setEditando(linha);
    setCodigo(linha?.codigo ?? '');
    setNome(linha?.nome ?? '');
    setEvitavel(linha === null ? '' : linha.evitavel ? 'sim' : 'nao');
    setAtivo(linha?.ativo ?? true);
    /* Na alteração o código já existe: ele não segue o nome. */
    setCodigoTocado(linha !== null);
    setErros(SEM_ERRO);
    setRecusa(null);
    setAberto(true);
  }

  function fechar() {
    setAberto(false);
    setEditando(null);
    setErros(SEM_ERRO);
    setRecusa(null);
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = Object.fromEntries(
      CAMPOS_COM_REGRA.map((campo) => [campo, mensagemDe(campo, { codigo, nome, evitavel })]),
    ) as Erros;
    setErros(achados);

    /* No envio, revalida tudo e leva o foco ao primeiro campo recusado. */
    const primeiro = CAMPOS_COM_REGRA.find((campo) => achados[campo] !== null);
    if (primeiro) {
      document.getElementById(`${base}-${primeiro}`)?.focus();
      return;
    }

    setRecusa(null);
    gravar.mutate(
      {
        id: editando?.id ?? null,
        envio: {
          codigo: codigo.trim(),
          nome: nome.trim(),
          evitavel: evitavel === 'sim',
          ativo,
          motivo: editando === null ? 'cadastro da causa de parada' : 'alteração da causa de parada',
          /* A versao de quando o formulario abriu (robos de 20/10/2026). */
          ...(editando?.versao ? { versao: editando.versao } : {}),
        },
      },
      {
        onSuccess: fechar,
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const COLUNAS: readonly Coluna<MotivoDaApi>[] = [
    {
      chave: 'causa',
      rotulo: 'Causa',
      celula: (m) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{m.nome}</span>
          <span className="mono txt-apoio text-tinta-secundaria">{m.codigo}</span>
        </span>
      ),
    },
    {
      chave: 'evitavel',
      rotulo: 'Evitável',
      alinhamento: 'status',
      celula: (m) => (
        <span className="txt-corpo text-tinta">{m.evitavel ? 'Evitável' : 'Não evitável'}</span>
      ),
    },
    {
      chave: 'ativo',
      rotulo: 'Situação',
      alinhamento: 'status',
      /* RÓTULO ESCRITO, e a grafia é a que o resto dos cadastros usa: "Em uso"
         e "Arquivado", e não "ativo/inativo" — que não existem no sistema. */
      celula: (m) => (
        <span className="txt-corpo text-tinta">{m.ativo ? 'Em uso' : 'Arquivado'}</span>
      ),
    },
  ];

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        {/* O TÍTULO É O NOME DO MENU E DO BOTÃO QUE TRAZ ATÉ AQUI ("Causas e
            turno"; p.23): a tela abria por um nome e se chamava por outro. As
            duas partes dela viram seções com título. */}
        <CabecalhoPagina
          sobretitulo="Operação"
          titulo="Causas e turno"
          subtitulo="O vocabulário das paradas e as horas que cada máquina deveria trabalhar"
          acoes={
            /* UMA AÇÃO EM LARANJA POR TELA: com a janela aberta, a página atrás
               fica bloqueada e o laranja que se alcança é o da janela. */
            podeGerir ? (
              <Botao
                nivel="primario"
                onClick={() => {
                  abrir(null);
                }}
              >
                Cadastrar causa
              </Botao>
            ) : undefined
          }
        />

        <div className="flex flex-col gap-2">
          <h2 className="txt-secao text-tinta">Causas de parada</h2>
          <p className="txt-apoio max-w-[68ch] text-pretty text-tinta-secundaria">
            Parada e Em manutenção exigem uma causa escolhida nesta lista. O texto do motivo continua
            sendo escrito na hora: a causa agrupa, o texto descreve o caso. E “evitável” separa o que
            a operação pode atacar — chuva e frente não liberada entram nas horas paradas e ficam
            fora da conta de paradas evitáveis.
          </p>
        </div>

        {/* MODAL AO CENTRO (R01, p.29): três ou quatro campos são tarefa curta,
            e não formulário no fluxo da página. Antes ele abria no topo, acima
            da tabela: "Alterar causa" na última linha levava a pessoa para longe
            da linha que ela tinha escolhido — o defeito que a R01 diagnostica. */}
        <Janela
          titulo={editando === null ? 'Cadastrar causa' : 'Alterar causa'}
          aberta={aberto && podeGerir}
          aoFechar={fechar}
          /* Rodapé fixo da janela (R01, p.29): ação principal à direita,
             Cancelar à esquerda dela. A janela leva o foco ao primeiro campo. */
          acoes={
            <>
              <Botao nivel="secundario" type="button" onClick={fechar}>
                Cancelar
              </Botao>
              <Botao nivel="primario" type="submit" form={`${base}-form`} disabled={gravar.isPending}>
                {gravar.isPending ? 'Gravando…' : editando === null ? 'Cadastrar causa' : 'Gravar alteração'}
              </Botao>
            </>
          }
        >
          <form id={`${base}-form`} onSubmit={enviar} className="formulario-grade">
            <div className="campo-largo">
              <Campo id={`${base}-nome`} rotulo="Nome" erro={erros.nome}>
                <input
                  id={`${base}-nome`}
                  type="text"
                  autoComplete="off"
                  className="controle w-full"
                  placeholder="Bomba hidráulica"
                  value={nome}
                  {...invalido('nome')}
                  onChange={(e) => {
                    setNome(e.target.value);
                    if (!codigoTocado) {
                      setCodigo(codigoDoNome(e.target.value));
                      limpar('codigo');
                    }
                    limpar('nome');
                  }}
                  onBlur={() => {
                    conferir('nome');
                  }}
                />
              </Campo>
            </div>

            <Campo
              id={`${base}-codigo`}
              rotulo="Código"
              erro={erros.codigo}
              preenchido={!codigoTocado && codigo !== ''}
              {...(!codigoTocado && codigo !== '' ? { ajuda: 'Saiu do nome. Troque se a empresa usa outro.' } : {})}
            >
              <input
                id={`${base}-codigo`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                placeholder="BOMBA-HIDRAULICA"
                value={codigo}
                {...invalido('codigo')}
                onChange={(e) => {
                  setCodigo(e.target.value.toUpperCase());
                  setCodigoTocado(true);
                  limpar('codigo');
                }}
                onBlur={() => {
                  conferir('codigo');
                }}
              />
            </Campo>

            {/* OPÇÃO CURTA, e a explicação na ajuda de 12 px abaixo do campo
                (R01, p.28). Com a explicação dentro do rótulo da opção, o valor
                escolhido não cabia no campo e era cortado no meio. */}
            <Campo
              id={`${base}-evitavel`}
              rotulo="Evitável"
              ajuda="Evitável é o que a operação pode atacar. Chuva, cliente e terceiros não são evitáveis."
              erro={erros.evitavel}
            >
              <select
                id={`${base}-evitavel`}
                className="controle w-full"
                value={evitavel}
                {...invalido('evitavel')}
                onChange={(e) => {
                  setEvitavel(e.target.value as '' | 'sim' | 'nao');
                  limpar('evitavel');
                }}
                onBlur={() => {
                  conferir('evitavel');
                }}
              >
                {/* SEM PADRÃO. Com "não" padrão o relatório de evitáveis
                    nasceria vazio; com "sim", chuva entraria nele. Quem
                    cadastra a causa é quem sabe de qual das duas ela é. */}
                <option value="">Escolha</option>
                <option value="sim">Evitável</option>
                <option value="nao">Não evitável</option>
              </select>
            </Campo>

            {editando === null ? null : (
              <Campo
                id={`${base}-ativo`}
                rotulo="Situação"
                ajuda="A causa arquivada some do seletor e continua no histórico."
              >
                <select
                  id={`${base}-ativo`}
                  className="controle w-full"
                  value={ativo ? 'sim' : 'nao'}
                  onChange={(e) => {
                    setAtivo(e.target.value === 'sim');
                  }}
                >
                  <option value="sim">Em uso</option>
                  <option value="nao">Arquivado</option>
                </select>
              </Campo>
            )}

            {recusa === null ? null : (
              <div
                role="alert"
                className="campo-linha flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4"
              >
                <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
                <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
              </div>
            )}

          </form>
        </Janela>

        <Tabela
          rotulo="Causas de parada"
          colunas={COLUNAS}
          linhas={visiveis}
          alturaDupla
          chaveDaLinha={(m) => m.id}
          /* A LINHA DE ORIGEM FICA MARCADA enquanto a janela dela está aberta (p.19). */
          selecionada={aberto && editando !== null ? editando.id : null}
          /* A AÇÃO DA LINHA na última célula, à direita e no hover (p.18) — e
             não numa coluna "Ação" própria, com oito links laranja sempre
             acesos competindo com o "Cadastrar causa". */
          {...(podeGerir
            ? {
                acoes: (m: MotivoDaApi) => (
                  <Botao
                    nivel="terciario"
                    onClick={() => {
                      abrir(m);
                    }}
                  >
                    Alterar causa
                  </Botao>
                ),
              }
            : {})}
          carregando={motivos.isPending}
          erro={
            motivos.isError ? (
              <EstadoVazio
                titulo="Não foi possível carregar as causas"
                descricao={explicarFalha(motivos.error).comoResolver}
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      void motivos.refetch();
                    }}
                  >
                    Carregar de novo
                  </Botao>
                }
              />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhuma causa cadastrada"
              descricao="Enquanto não houver causa nesta lista, não é possível registrar parada nem manutenção — é ela que permite agrupar as horas paradas e ver o que atacar primeiro."
              acao={
                podeGerir ? (
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      abrir(null);
                    }}
                  >
                    Cadastrar primeira causa
                  </Botao>
                ) : undefined
              }
            />
          }
          /* RODAPÉ DA p.18: "Mostrando 1–8 de 8" à esquerda, paginação ao
             centro, itens por página à direita — como nas outras listas. */
          rodape={<RodapeTabela {...rodape} />}
        />

        <TurnoDaEmpresa />
      </div>
    </div>
  );
}
